/**
 * Reads the plain text out of a Word .docx transcript.
 *
 * Only the words matter here. Word styling, fonts, colours, headers, footers
 * and page layout are all discarded — Portal holds a transcript, not a
 * document. Paragraph breaks and speaker labels survive because they are what
 * makes a transcript readable.
 *
 * The parser is imported on demand, so it is only fetched when someone
 * actually chooses a .docx file.
 */
export async function extractDocxText(file: File): Promise<string> {
  const buffer = await file.arrayBuffer()

  const mammoth = await import('mammoth/mammoth.browser')
  const extract = (mammoth as unknown as {
    extractRawText: (input: { arrayBuffer: ArrayBuffer }) => Promise<{ value: string }>
  }).extractRawText

  const { value } = await extract({ arrayBuffer: buffer })
  return tidy(value)
}

/**
 * Word leaves empty paragraphs and trailing spaces behind. This keeps real
 * paragraph breaks, drops the padding, and changes no wording.
 */
function tidy(raw: string): string {
  return raw
    .replace(/\r\n?/g, '\n')
    .split('\n')
    .map((line) => line.replace(/[ \t\u00a0]+$/g, '').replace(/\u00a0/g, ' '))
    .join('\n')
    /* At most one blank line between paragraphs. */
    .replace(/\n{3,}/g, '\n\n')
    .trim()
}

export function isDocx(file: File): boolean {
  return (
    file.name.toLowerCase().endsWith('.docx') ||
    file.type === 'application/vnd.openxmlformats-officedocument.wordprocessingml.document'
  )
}

/** Word's older binary format cannot be read in the browser. */
export function isLegacyDoc(file: File): boolean {
  return file.name.toLowerCase().endsWith('.doc') && !isDocx(file)
}
